import { CoffeeShop } from '../hooks/useCoffeeShops';

export interface TopDrink {
    type: string;
    averageRating: number;
    count: number;
    lastRated: string;
    iconName: string;
    displayName: string;
}

const SHOP_TYPE_LABELS: Record<string, string> = {
    independent: 'Independent',
    'coffee-chain': 'Chain',
    roastery: 'Roastery',
    'bakery-cafe': 'Bakery & Cafe',
    specialty: 'Specialty'
};

/**
 * Show only curated shopType labels. The raw `types` array often contains
 * generic Google Places categories ("cafe", "food", "point_of_interest") that
 * read like noise on a card — keeping just the chain-or-not classification.
 */
export function shopTypeTags(shop: CoffeeShop): string[] {
    const out: string[] = [];
    if (shop.shopType && SHOP_TYPE_LABELS[shop.shopType]) {
        out.push(SHOP_TYPE_LABELS[shop.shopType]);
    }
    if (shop.chainName) out.push(shop.chainName);
    return out.slice(0, 2);
}

export function topDrinkOf(
    shop: CoffeeShop,
    coffeeTypesCache: Record<string, { iconName: string; displayName: string }>
): TopDrink | null {
    const entries = Object.entries(shop.coffeeTypeStats || {});
    if (!entries.length) return null;
    const sorted = [...entries].sort((a, b) => {
        const ar = a[1].averageRating;
        const br = b[1].averageRating;
        if (br !== ar) return br - ar;
        return b[1].count - a[1].count;
    });
    const [type, stats] = sorted[0];
    const meta = coffeeTypesCache[type];
    return {
        type,
        averageRating: stats.averageRating,
        count: stats.count,
        lastRated: stats.lastRated,
        iconName: meta?.iconName || 'espresso',
        displayName: meta?.displayName || type
    };
}

/** Most recent rating timestamp across all coffee types on this shop. */
export function lastRatedAt(shop: CoffeeShop): string | null {
    const stamps = Object.values(shop.coffeeTypeStats || {})
        .map(s => s.lastRated)
        .filter(Boolean);
    if (!stamps.length) return null;
    return stamps.reduce((latest, cur) => (cur > latest ? cur : latest));
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatStamp(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, '0')}, ${d.getFullYear()}`;
}

/** € repeated for priceLevel; returns null when level missing. */
export function priceMarks(level?: number): { filled: string; faded: string } | null {
    if (!level || level < 1 || level > 4) return null;
    return {
        filled: '€'.repeat(level),
        faded: '€'.repeat(4 - level)
    };
}
